import { File05Icon } from '@langchain/untitled-ui-icons';

import { ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import { match } from 'ts-pattern';

import { Pane } from '@/components/Pane';
import { Button } from '@/design-system';
import { useSegmentEventWrapper } from '@/hooks/useSegmentEventWrapper';

import { OnboardingChecklistCardA } from './OnboardingChecklistCardA';
import { OnboardingChecklistCardB } from './OnboardingChecklistCardB';

export interface OnboardingChecklistCardProps {
  title: string;
  description: string;
  isCompleted: boolean;
  segmentEventButtonName: string;
  img: ReactNode;
}

type OnboardingChecklistCardRendererProps = OnboardingChecklistCardProps & {
  variant: 'treatment_a' | 'treatment_b';
} & (
    | {
        to: string;
      }
    | {
        panel: React.ReactNode;
        panelTitle?: string;
        docsLink?: string;
        onPanelOpen?: () => void;
        onPanelClose?: () => void;
      }
  );

export function OnboardingChecklistCardRenderer(
  props: OnboardingChecklistCardRendererProps
) {
  const [open, setOpen] = useState(false);

  const track = useSegmentEventWrapper(() => {
    return [
      'home_get_started_button_click',
      {
        button_name: props.segmentEventButtonName,
      },
    ];
  });

  const handleClick = track(() => {
    if ('to' in props) {
      window.location.href = props.to;
    } else {
      setOpen(true);
      props.onPanelOpen?.();
    }
  });

  const card = match(props.variant)
    .with('treatment_a', () => <OnboardingChecklistCardA {...props} />)
    .with('treatment_b', () => <OnboardingChecklistCardB {...props} />)
    .exhaustive();

  return (
    <div
      className={match(props.variant)
        .with('treatment_a', () => 'w-1/3 min-w-[16rem] max-w-[24rem]')
        .with('treatment_b', () => 'w-1/3 min-w-[20rem] max-w-[24rem]')
        .exhaustive()}
    >
      {'to' in props ? (
        <Link to={props.to} className="rounded-[20px]" onClick={handleClick}>
          {card}
        </Link>
      ) : (
        <button
          type="button"
          className="h-full w-full rounded-[20px] text-left"
          onClick={handleClick}
        >
          {card}
        </button>
      )}
      {'panel' in props && (
        <Pane
          title={props.panelTitle || props.title}
          topBarRightElement={
            props.docsLink ? (
              <Link to={props.docsLink} target="_blank">
                <Button
                  leftDecorator={File05Icon}
                  color="secondary"
                  variant="outlined"
                  size="sm"
                  className="mr-6"
                >
                  Docs
                </Button>
              </Link>
            ) : undefined
          }
          open={open}
          onClose={() => {
            setOpen(false);
            props.onPanelClose?.();
          }}
        >
          {props.panel}
        </Pane>
      )}
    </div>
  );
}
